/**
 * What stands outside, and whether it still shows what the app shows now (S62, Z-0; 25-zeitsteuerung.md
 * § 10.5): the QR-Code-Speisekarte, the Empfehlungskarte above it, the fixed drinks menu (S59), the PDF
 * links (S54) and the price list at the entrance (S42). One rule for all of it (Martin, 03.11.2026):
 * when outside shows an old state, the list of menus marks it with ⚠, and saving the menu says so.
 */
import { cardLangKey, cardLangOf, designFor, designOf, digestOf, freezeForNet, leadDigestOf, leadOf, menuView, partOf, withDrinksOf } from "@speisekarte/core";
import type { Menu, PdfSlot, Planned, PublishedLead } from "@speisekarte/core";
import { entranceNow, planPdfSlot, printMenuOf } from "./output.ts";
import { api, store } from "./store.ts";
import { whenText } from "./timing.ts";
import type { PrintMenu } from "./menuHtml.ts";
import { dishesById, textsById } from "./store.ts";
import type { Data } from "./store.ts";

/** Whether the menu on the net still shows what the menu shows now. */
export function outdated(data: Data): boolean {
  const p = data.published;
  if (!p || !data.menus.some((m) => m.id === p.menu)) return false;
  return netDigestNow(data, p.menu, data.settings.drinksMenu, data.settings.recommendationsMenu ?? "") !== p.digest;
}

/**
 * The mark of a menu as the server would freeze it now (`freeze`): with „Getränke aus Speisekarte …“
 * (S22) the drinks of that menu, with its recommendations above (S37), with the texts in the further
 * languages (S36). The menu on the net, or a folder of the weekly plan (S62) with its own parts.
 */
function netDigestNow(data: Data, menuId: string, drinksMenu: string, recommendations: string): string {
  const own = data.menus.find((m) => m.id === menuId)!;
  const menu = withDrinksOf(own, drinksMenu && drinksMenu !== own.id ? (data.menus.find((m) => m.id === drinksMenu) ?? null) : null);
  const design = designFor(data.designs, menu.style);
  const brand = { logo: design.logoHidden ? null : data.logo, logoOnly: design.logoOnly, logoPlace: design.logoPlace, design };
  const lead = leadOfMenu(data, recommendations, own.id);
  const frozen = freezeForNet(menu.id, menuView(menu, dishesById(data), data.allergens, textsById(data), data.tagIcons), data.settings.house || data.workspace, 0, { logo: null, logoOnly: false, design }, dishesById(data), data.settings.languages ?? [], data.phrases ?? {});
  return digestOf({ ...frozen, ...brand, ...(lead ? { lead } : {}) });
}

/** Whether a folder of the weekly plan (S62) shows on the net less than its menu now; one never frozen too. */
export function folderOld(f: NonNullable<Data["folders"]>[number], data: Data): boolean {
  if (!data.menus.some((m) => m.id === f.menu)) return false;
  if (!f.published) return true;
  return f.published.menu !== f.menu || netDigestNow(data, f.menu, f.drinksMenu, f.recommendations) !== f.published.digest;
}

/** Whether the recommendations on the net show less than their menu now (S37, L4), whatever the menu below did. */
export function recommendationOld(data: Data): boolean {
  const p = data.published;
  const lead = p ? leadNow(data, p.menu) : null;
  return Boolean(lead) && (p!.lead?.menu !== lead!.menu || p!.lead?.digest !== leadDigestOf(lead!));
}

/** The recommendations above the menu on the net as the server freezes them (S37, L2), or null. */
export function leadNow(data: Data, published: string): PublishedLead | null {
  return leadOfMenu(data, data.settings.recommendationsMenu ?? "", published);
}

/** Recommendations by their menu above a menu, as the server freezes them; none above itself. */
function leadOfMenu(data: Data, id: string, published: string): PublishedLead | null {
  const own = id && id !== published ? data.menus.find((m) => m.id === id) : null;
  return own ? leadOf(freezeForNet(own.id, menuView(own, dishesById(data), data.allergens, textsById(data), data.tagIcons), data.settings.house || data.workspace, 0, undefined, dishesById(data), data.settings.languages ?? [], data.phrases ?? {})) : null;
}

/**
 * The mark of a PDF (S62, Z-0): everything it shows, not when it was made. The logo by when it was
 * uploaded (the device need not hold it), the own pages by their files.
 */
export function pdfDigestOf(m: PrintMenu, data: Data): string {
  const logo = data.logo?.at ?? null;
  const own = m.ownPages ? [m.ownPages, [...m.ownPages.front, ...m.ownPages.back].map((id) => data.ownPages?.find((p) => p.id === id)?.file ?? "")] : null;
  // The style as `designOf` reads it and every object by its keys in order: the editor's draft and the saved menu give the same mark.
  const json = stable([m.view, m.house, m.footer, m.design?.logoHidden ? null : logo, m.design?.footLogo ? logo : null, m.logoOnly ?? false, m.logoPlace ?? null, m.design ? designOf(m.design) : null, m.small ?? false, m.links ?? "", own]);
  let h = 0x811c9dc5;
  for (let i = 0; i < json.length; i++) {
    h ^= json.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
}

/** JSON with every object's keys in order, so that how an object was built does not change its text. */
function stable(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`;
  if (v && typeof v === "object") return `{${Object.keys(v).sort().filter((k) => (v as Record<string, unknown>)[k] !== undefined).map((k) => `${JSON.stringify(k)}:${stable((v as Record<string, unknown>)[k])}`).join(",")}}`;
  return JSON.stringify(v ?? null);
}

/** The PDF a slot's menu would make now, in the slot's language; null when its menu is gone. */
export function slotPrintNow(slot: Pick<PdfSlot, "menuId" | "lang">, data: Data): PrintMenu | null {
  const menu = slot.menuId ? data.menus.find((m) => m.id === slot.menuId) : null;
  return menu ? printMenuOf(menu, data, new Date(0), undefined, menu.id, cardLangOf(slot.lang, data.settings.languages ?? [])) : null;
}

/** Whether a PDF link shows less than its menu now; one made before its mark (`digest` „“) is not known to be old. */
export function slotOld(slot: PdfSlot, data: Data): boolean {
  if (!slot.file || !slot.digest || !slot.menuId) return false;
  const now = slotPrintNow(slot, data);
  return Boolean(now) && pdfDigestOf(now!, data) !== slot.digest;
}

/** Whether a planned PDF (S62 § 10) shows less than its menu now; one without its mark is not known to be old. */
export function plannedPdfOld(p: Planned, data: Data): boolean {
  if (p.what !== "pdf" || !p.digest) return false;
  const now = slotPrintNow({ menuId: p.menu, lang: p.lang ?? "" }, data);
  return Boolean(now) && pdfDigestOf(now!, data) !== p.digest;
}

/** A planned PDF made again as the menu is now, for the same link and moment; the old plan goes after. */
export async function renewPlannedPdf(p: Planned, data: Data): Promise<void> {
  const now = slotPrintNow({ menuId: p.menu, lang: p.lang ?? "" }, data);
  if (!now || !p.slot) return;
  await planPdfSlot(now, data, p.slot, p.menu, p.at, pdfMarkOf(now, data, cardLangOf(p.lang ?? "", data.settings.languages ?? [])));
  await store.change(() => api(`/api/plan/${p.id}`, { method: "DELETE" }));
}

/** The mark and language a PDF is sent with (`publishPdfSlot`). */
export const pdfMarkOf = (m: PrintMenu, data: Data, lang: Parameters<typeof cardLangKey>[0]) => ({ digest: pdfDigestOf(m, data), lang: cardLangKey(lang) });

/** Whether a menu, or a dish in the given part of it, changed after a moment: to tell which of two menus on the net is behind. */
function changedSince(m: Menu, at: number, part: "food" | "drinks", data: Data): boolean {
  if (m.updatedAt > at) return true;
  const dishes = dishesById(data);
  return m.sections.some((s) => partOf(s) === part && s.items.some((i) => (dishes.get(i.dish)?.updatedAt ?? 0) > at));
}

export type OutsideKind = "qr" | "recommendations" | "drinks" | "pdf" | "entrance" | "folder";

/** One place outside where a menu stands, as its mark in the list of menus. */
export interface OutsideUse {
  kind: OutsideKind;
  label: string;
  /** Outside shows an old state. */
  old: boolean;
  /** The PDF link, for a PDF. */
  slot?: PdfSlot;
  /** Planned for later (S62): light green, „ab …“. */
  planned?: Planned;
  /** A folder of the weekly plan (S62, Stufe 2) the menu is part of. */
  folder?: string;
}

/**
 * Where each menu stands outside (S4, Nachtrag S59; S62 Z-0), by its id: on the net as the
 * QR-Code-Speisekarte, its Empfehlungskarte or its fixed drinks menu, in a PDF link (by the link's
 * name), at the entrance; each with whether it is old. What the net shows is compared once for all.
 * The server keeps such a menu (`menusInUse`): no ✕, not cleared up.
 */
export function outsideAll(data: Data): Map<string, OutsideUse[]> {
  const s = data.settings;
  const p = data.published;
  const netOld = p ? outdated(data) : false;
  const recOld = p ? recommendationOld(data) : false;
  // The drinks of another menu (S22) on the QR-Code-Speisekarte: behind is the one that changed.
  const drinks = s.drinksMenu && p && s.drinksMenu !== p.menu ? data.menus.find((x) => x.id === s.drinksMenu) : null;
  const drinksOld = Boolean(drinks) && netOld && changedSince(drinks!, p!.publishedAt, "drinks", data);
  const main = p ? data.menus.find((x) => x.id === p.menu) : null;
  const qrOld = netOld && !(drinksOld && main && !changedSince(main, p!.publishedAt, "food", data));
  const entrance = s.entranceMenu ? entranceNow(data) : null;
  const all = new Map<string, OutsideUse[]>();
  for (const m of data.menus) {
    const uses: OutsideUse[] = [];
    if (p && m.id === p.menu) uses.push({ kind: "qr", label: "QR-Code-Speisekarte", old: qrOld });
    if (m.id === s.recommendationsMenu) uses.push({ kind: "recommendations", label: "Empfehlungskarte", old: recOld });
    if (m.id === s.drinksMenu) uses.push({ kind: "drinks", label: "Getränkekarte", old: drinksOld });
    for (const slot of (data.pdfSlots ?? []).filter((x) => x.menuId === m.id)) uses.push({ kind: "pdf", label: `PDF-Link ${slot.name}`, old: slotOld(slot, data), slot });
    if (m.id === s.entranceMenu) uses.push({ kind: "entrance", label: "Aushang am Eingang", old: s.entrancePrinted?.menu !== m.id || s.entrancePrinted?.digest !== entrance });
    // In a folder of the weekly plan (S62, Stufe 2): „Mappe Mittag“, with ⚠ when its frozen state is behind.
    // Also while the plan rests (below „Erweitert“): the folder keeps the menu, as the server does (`menusInUse`); ⚠ only when it shows.
    for (const f of data.folders ?? []) if ([f.menu, f.recommendations, f.drinksMenu].includes(m.id)) uses.push({ kind: "folder", label: `Mappe ${f.name}`, old: data.settings.timing === "extended" && folderOld(f, data), folder: f.id });
    // Planned for later (S62): „PDF-Link Speisekarte ab 1.12.“, a planned PDF that is behind with ⚠.
    for (const p of (s.planned ?? []).filter((x) => x.menu === m.id).sort((a, b) => a.at - b.at)) {
      const slot = p.slot ? (data.pdfSlots ?? []).find((x) => x.id === p.slot) : undefined;
      const label = `${p.what === "menu" ? "QR-Code-Speisekarte" : p.what === "recommendations" ? "Empfehlungskarte" : `PDF-Link ${slot?.name ?? ""}`.trim()} ab ${whenText(p.at, Date.now())}`;
      uses.push({ kind: p.what === "menu" ? "qr" : p.what === "recommendations" ? "recommendations" : "pdf", label, old: plannedPdfOld(p, data), planned: p, ...(slot ? { slot } : {}) });
    }
    all.set(m.id, uses);
  }
  return all;
}

/** Where one menu stands outside (`outsideAll`). */
export const outsideOf = (m: Pick<Menu, "id">, data: Data): OutsideUse[] => outsideAll(data).get(m.id) ?? [];
